import React, { useCallback, useEffect, useRef, useState } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Alert, 
  Stack, 
  TextField, 
  IconButton, 
  Tooltip,
  CircularProgress 
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DownloadIcon from '@mui/icons-material/Download';
import { apiClient, CliTokenStatus, IssueCliTokenResponse } from '../../services/api';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';

// Dummy GitHub releases URL – replace with actual CLI binary releases when available
const GITHUB_BINARY_RELEASES_URL = 'https://github.com/amulyakashyap09/baastream-public/tree/master/assets';

function toIso(val: any): string | null {
  if (!val) return null;
  try {
    if (val instanceof Date) {
      return isNaN(val.getTime()) ? null : val.toISOString();
    }
    if (typeof val === 'string' || typeof val === 'number') {
      const d = new Date(val);
      return isNaN(d.getTime()) ? null : d.toISOString();
    }
    if (typeof val === 'object') {
      if (typeof (val as any).toDate === 'function') {
        const d = (val as any).toDate();
        return isNaN(d.getTime()) ? null : d.toISOString();
      }
      if ('_seconds' in val) {
        const seconds = (val as any)._seconds ?? (val as any).seconds;
        const nanos = (val as any)._nanoseconds ?? (val as any).nanoseconds ?? 0;
        if (typeof seconds === 'number') {
          const ms = seconds * 1000 + Math.floor(nanos / 1e6);
          const d = new Date(ms);
          return isNaN(d.getTime()) ? null : d.toISOString();
        }
      }
    }
  } catch {}
  return null;
}

export function CLIToken() {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid || null;
  const [status, setStatus] = useState<CliTokenStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [issued, setIssued] = useState<IssueCliTokenResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showToken, setShowToken] = useState<boolean>(false);
  const fetchedForUid = useRef<string | null>(null);

  const tokenKey = uid ? `cliToken:${uid}` : null;
  const tokenExpiryKey = uid ? `cliTokenExpiresAt:${uid}` : null;

  const clearStoredToken = useCallback(() => {
    if (!tokenKey || !tokenExpiryKey) return;
    try {
      localStorage.removeItem(tokenKey);
      localStorage.removeItem(tokenExpiryKey);
    } catch {}
  }, [tokenKey, tokenExpiryKey]);

  const refresh = useCallback(async (preserveIssued = false) => {
    setError(null);
    if (!preserveIssued) {
      setIssued(null);
    } else {
    }
    setStatus(null);
    try {
      const s = await apiClient.getCurrentCliTokenStatus();

      setStatus({
        ...s,
        canIssue: typeof s.canIssue === 'boolean' ? s.canIssue : !s.hasActiveToken,
        expiresAt: toIso(s.expiresAt),
      });

      // If backend says no active token, clear any locally stored raw token.
      if (!s?.hasActiveToken) {
        clearStoredToken();
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'Failed to load token status');
    } finally {
      setInitialLoading(false);
    }
  }, [clearStoredToken]);

  useEffect(() => {
    if (!uid) return;
    if (fetchedForUid.current === uid) return;
    fetchedForUid.current = uid;

    // Restore token from localStorage (scoped per user) so it stays visible after refresh.
    try {
      if (tokenKey && tokenExpiryKey) {
        const stored = localStorage.getItem(tokenKey);
        const storedExpiry = localStorage.getItem(tokenExpiryKey);
        if (stored && storedExpiry) {
          const expiryDate = new Date(storedExpiry);
          const now = new Date();
          if (expiryDate > now) {
            setIssued({ token: stored, expiresAt: storedExpiry, status: 'active' });
          } else {
            clearStoredToken();
          }
        } else {
          console.error('ℹ️ No token found in localStorage');
        }
      }
    } catch (e) {
      console.error('❌ Error restoring token from localStorage:', e);
    }

    refresh(true);
  }, [uid, tokenKey, tokenExpiryKey, clearStoredToken, refresh]);

  const handleIssue = async () => {
    setLoading(true);
    setError(null);
    setIssued(null);
    try {
      const res = await apiClient.issueCliToken();
      const expires = toIso(res.expiresAt) || new Date().toISOString();

      setIssued({ ...res, expiresAt: expires });
      
      // Persist per-user so it remains visible after refresh (without leaking across accounts).
      try {
        if (tokenKey && tokenExpiryKey) {
          localStorage.setItem(tokenKey, res.token);
          localStorage.setItem(tokenExpiryKey, expires);
        }
      } catch (e) {
        console.error('❌ Failed to save token to localStorage:', e);
      }
      // Preserve the issued token when refreshing status
      await refresh(true);
    } catch (e: any) {
      console.error('❌ Failed to issue token:', e);
      setError(e?.response?.data?.message || e.message || 'Failed to issue token');
    } finally {
      setLoading(false);
    }
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {}
  };

  return (
    <Box sx={{ minHeight: '400px', width: '100%' }}>
      <Typography variant="h4" gutterBottom>CLI Access Token</Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Use this token with the CLI. Tokens are valid for 30 days. Store it securely; it will not be shown again.
      </Typography>

      {initialLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2} sx={{ mt: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Download Binary</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Download the CLI binary for your platform from GitHub releases.
              </Typography>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => window.open(GITHUB_BINARY_RELEASES_URL, '_blank', 'noopener,noreferrer')}
              >
                Download Binary
              </Button>
            </CardContent>
          </Card>

          {issued && issued.token && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>✅ CLI Token Generated</Typography>
              <Alert severity="success" sx={{ mb: 2 }}>
                Your CLI token has been generated. Copy it now - it won't be shown again after you leave this page.
              </Alert>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  fullWidth
                  value={showToken ? issued.token : '•'.repeat(Math.min(issued.token.length, 24))}
                  InputProps={{ readOnly: true }}
                />
                <Tooltip title={showToken ? 'Hide' : 'Show'}>
                  <span>
                    <IconButton onClick={() => setShowToken(v => !v)} disabled={!issued?.token}>
                      {showToken ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Copy">
                  <IconButton onClick={() => copy(issued.token)}>
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Expires: {format(new Date(issued.expiresAt), 'PPpp')}
              </Typography>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Current Status</Typography>
            <Typography variant="body2" gutterBottom>
              {status?.hasActiveToken ? 'Active token present' : 'No active token'}
            </Typography>
            {status?.expiresAt && (
              <Typography variant="body2" color="text.secondary">
                Expires: {format(new Date(status.expiresAt), 'PPpp')}
              </Typography>
            )}
            
            {status?.hasActiveToken && !issued?.token && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  You have an active token, but it's not displayed here for security reasons. 
                  Tokens are only shown once when generated.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Lost your token?</strong> Click "Rotate Token" below to generate a new one.
                </Typography>
              </Alert>
            )}
            
            <Box sx={{ mt: 2 }}>
              <Tooltip
                title={status?.hasActiveToken && status?.canRotate === false ? 'Token rotation is not available during the trial period. Upgrade to rotate.' : ''}
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={handleIssue}
                    disabled={loading || (status?.hasActiveToken ? status?.canRotate === false : !status?.canIssue)}
                  >
                    {status?.hasActiveToken ? 'Rotate Token (Generate New)' : 'Generate 30-day Token'}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </CardContent>
        </Card>
        </Stack>
      )}
    </Box>
  );
}
